import { useCallback, useMemo } from 'react';

import { useGetMasterClassWithPatientCasesQuery } from '@/@generated/codegen/API';

/**
 * Case metadata returned from GraphQL.
 * Note: Case content (stringifiedContent) is loaded separately via getPatientCaseWithContent.
 */
export interface MasterclassCaseMetadata {
  /** GraphQL ID */
  id: string;
  /** Legacy case identifier (used in URLs) */
  legacyCaseId: string;
  /** Display name for the case */
  name: string;
}

interface UseCasesForMasterclassResult {
  /** List of cases belonging to this masterclass */
  cases: MasterclassCaseMetadata[];
  /** Title of the masterclass */
  masterclassTitle: string | undefined;
  /** Whether the query is loading */
  loading: boolean;
  /** Error from the query, if any */
  error: Error | undefined;
  /** Find case by GraphQL ID */
  getCaseById: (id: string) => MasterclassCaseMetadata | undefined;
  /** Find case by legacy case ID */
  getCaseByLegacyId: (legacyCaseId: string) => MasterclassCaseMetadata | undefined;
}

/**
 * Hook to fetch cases for a specific masterclass via GraphQL.
 *
 * This returns case metadata (id, legacyCaseId, name) without the full case content.
 * Case content (stringifiedContent) should be loaded separately when needed
 * using getPatientCaseWithContent query.
 *
 * @param masterclassId - The GraphQL ID of the masterclass (undefined skips the query)
 * @returns Cases metadata, masterclass title, loading state, error, and lookup functions
 */
export const useCasesForMasterclass = (
  masterclassId: string | undefined
): UseCasesForMasterclassResult => {
  const { data, loading, error } = useGetMasterClassWithPatientCasesQuery({
    variables: {
      where: { id: masterclassId! },
      patientCasesPaginationArgs: { first: 100 },
    },
    skip: !masterclassId,
  });

  const cases: MasterclassCaseMetadata[] = useMemo(() => {
    if (!data?.getMasterClass?.patientCases?.edges) return [];

    return data.getMasterClass.patientCases.edges.map((edge) => ({
      id: edge.node.id,
      legacyCaseId: edge.node.legacyCaseId,
      name: edge.node.name,
    }));
  }, [data]);

  const getCaseById = useCallback(
    (id: string): MasterclassCaseMetadata | undefined => {
      return cases.find((c) => c.id === id);
    },
    [cases]
  );

  const getCaseByLegacyId = useCallback(
    (legacyCaseId: string): MasterclassCaseMetadata | undefined => {
      return cases.find((c) => c.legacyCaseId === legacyCaseId);
    },
    [cases]
  );

  return {
    cases,
    masterclassTitle: data?.getMasterClass?.title,
    loading,
    error: error as Error | undefined,
    getCaseById,
    getCaseByLegacyId,
  };
};
